import React, { useState, useCallback, useRef, useEffect, ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import { COLORS } from "../../assets/theme";
import SearchIcon from "@mui/icons-material/Search";
import CustomTextField from "../CustomTextField";
import CustomButton, { ExportProgressReporter } from "../CustomButton";
import CustomSelect from "../CustomSelect";
import DateRangePicker from "../DateRangePicker";
import CustomLabel from "../CustomLabel";

type SelectConfig = {
  options: { label: string; value: string | number }[];
  value: string | number | (string | number)[];
  onChange: (v: any) => void;
  placeholder?: string;
  size?: "small" | "medium" | "large";
  clearable?: boolean;
  searchable?: boolean;
  multiSelect?: boolean;
  label?: string;
  hidden?: boolean;
  sx?: any;
};

interface ISearchHeaderProps {
  search: string;
  setSearch: (search: string) => void;
  buttonConfig?: {
    label?: string;
    /** May return a promise; exports report progress through the argument. */
    onClick: (reportProgress: ExportProgressReporter) => void | Promise<void>;
    minHeight?: number;
    minWidth?: number | string;
    [key: string]: any;
  }[];
  selectConfig?: SelectConfig | null;
  selectConfig2?: SelectConfig | null;
  /** Further selects, rendered after selectConfig and selectConfig2. */
  extraSelects?: SelectConfig[];
  /** Inline controls shown on the same row, right after the selects. */
  extraFilters?: ReactNode;
  dateRangeConfig?: {
    value?: { start?: string; end?: string } | [string?, string?];
    onChange: (v: any) => void;
    size?: "small" | "medium";
    fullWidth?: boolean;
  } | null;
}

function SearchHeaderContainer({
  search,
  setSearch,
  buttonConfig = [],
  selectConfig = null,
  selectConfig2 = null,
  extraSelects = [],
  extraFilters = null,
  dateRangeConfig = null,
}: ISearchHeaderProps) {
  const [localSearch, setLocalSearch] = useState(search);
  const debounceRef = useRef<number | undefined>(undefined);

  const handleDebouncedChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setLocalSearch(value);
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
      debounceRef.current = window.setTimeout(() => setSearch(value), 300);
    },
    [setSearch]
  );

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const selects = [selectConfig, selectConfig2, ...extraSelects].filter(
    (config): config is SelectConfig => Boolean(config)
  );

  const renderSelect = (
    config: SelectConfig,
    key: string,
    boxSx: Record<string, any> = {}
  ) => (
    <Box
      key={key}
      sx={{
        width: { xs: "100%", sm: 160, md: 180 },
        maxWidth: 180,
        // Above phone width the selects stay on one line, narrowing if needed.
        flexShrink: { xs: 0, sm: 1 },
        minWidth: { sm: 120 },
        position: "relative",
        ...boxSx,
      }}
    >
      {config.label && (
        <CustomLabel
          text={config.label}
          sx={{
            position: "absolute",
            top: -22,
            left: 0,
            backgroundColor: COLORS.MainBackground,
            px: 0,
            fontSize: "0.95rem",
            pointerEvents: "none",
          }}
        />
      )}
      <CustomSelect
        options={config.options}
        value={config.value}
        onChange={config.onChange}
        placeholder={config.placeholder}
        size={config.size}
        hidden={config.hidden}
        label={config.label}
        searchable={config.searchable}
        multiSelect={config.multiSelect}
        clearable={config.clearable}
        sx={{ ...(config.sx || {}), width: "100%" }}
      />
    </Box>
  );

  return (
    <Box
      mb={1}
      display="flex"
      alignItems="center"
      flexWrap="wrap"
      sx={{
        position: "relative",
        gap: 1,
        width: "100%",
        px: { xs: 1, sm: 0 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          width: { xs: "100%", sm: "auto" },
          mr: 1,
          mt: 2.6,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <CustomTextField
          size="small"
          startIcon={<SearchIcon />}
          placeholder="Search"
          bgColor={COLORS.WhiteBackground}
          value={localSearch}
          onChange={handleDebouncedChange}
          sx={{
            width: { xs: "100%", sm: 200, md: 220, lg: 240 },
            maxWidth: 240,
          }}
        />

        {selects.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              // Phones stack the selects; leave room for their floating labels.
              rowGap: selects.some((config) => config.label) ? 3.5 : 1,
              alignItems: "center",
              mt: { xs: 1, sm: 0 },
              width: { xs: "100%", sm: "auto" },
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            {selectConfig && renderSelect(selectConfig, "select-1")}
            {selectConfig2 &&
              renderSelect(selectConfig2, "select-2", {
                backgroundColor: "white",
              })}
            {extraSelects.map((config, index) =>
              renderSelect(config, `select-extra-${index}`)
            )}
          </Box>
        )}

        {extraFilters && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {extraFilters}
          </Box>
        )}
      </Box>

      {dateRangeConfig && (
        <Box
          sx={{
            width: { xs: "100%", sm: 220, md: 260 },
            ml: { xs: 0, sm: 0 },
            alignSelf: { xs: "center", sm: "flex-start" },
            mt: { xs: 1, sm: 0 },
            maxWidth: { xs: "100%", sm: 320 },
          }}
        >
          <DateRangePicker
            value={dateRangeConfig.value}
            onChange={dateRangeConfig.onChange}
            size={dateRangeConfig.size}
          />
        </Box>
      )}

      {buttonConfig.length > 0 && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            ml: { xs: 0, sm: "auto" },
            alignItems: "center",
            mt: 2.5,
            flexDirection: { xs: "column", sm: "row" },
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "center", sm: "flex-start" },
          }}
        >
          {buttonConfig.map((button, index) => {
            const mergedSx: any = {
              width: { xs: "100%", sm: "auto" },
              ...(button.sx || {}),
            };
            if (button.minWidth !== undefined) {
              mergedSx.minWidth = button.minWidth;
            }
            if (button.maxWidth !== undefined) {
              mergedSx.maxWidth = button.maxWidth;
            }

            return (
              <CustomButton
                key={index}
                {...button}
                minHeight={button.minHeight}
                minWidth={
                  typeof button.minWidth === "number"
                    ? button.minWidth
                    : undefined
                }
                disabled={button.disabled}
                sx={mergedSx}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default SearchHeaderContainer;
